/**
 * Subscribes to a server-sent events endpoint of the admin API (`/admin/v1/...`) with the staff
 * token (EventSource cannot send an Authorization header, so this reads the stream with fetch),
 * reconnecting with `Last-Event-ID` after a drop. `onEvent` gets each message's parsed JSON.
 * Not used with the in-browser mock API.
 */

import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../api';
import { getToken } from '../api/session';
import { USE_MOCKS } from '../config/env';

export type LiveState = 'off' | 'connecting' | 'live' | 'reconnecting';

export function useLiveEvents(path: string | null, onEvent: (data: unknown) => void): LiveState {
  const [state, setState] = useState<LiveState>('off');
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  }, [onEvent]);
  useEffect(() => {
    if (!path || USE_MOCKS) return undefined;
    const controller = new AbortController();
    let lastId = '';
    const run = async () => {
      let delay = 1000;
      while (!controller.signal.aborted) {
        setState(lastId ? 'reconnecting' : 'connecting');
        try {
          const headers: Record<string, string> = { Accept: 'text/event-stream' };
          const token = getToken();
          if (token) headers.Authorization = `Bearer ${token}`;
          if (lastId) headers['Last-Event-ID'] = lastId;
          const res = await fetch(`${API_BASE}${path}`, { headers, signal: controller.signal });
          if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
          setState('live');
          delay = 1000;
          const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
          let buffer = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += value;
            let cut = buffer.indexOf('\n\n');
            while (cut >= 0) {
              const block = buffer.slice(0, cut);
              buffer = buffer.slice(cut + 2);
              let data = '';
              for (const line of block.split('\n')) {
                if (line.startsWith('id:')) lastId = line.slice(3).trim();
                else if (line.startsWith('data:')) data += line.slice(5).trimStart();
              }
              if (data) {
                try {
                  handler.current(JSON.parse(data));
                } catch {
                  // Ignore a malformed message; the next one carries the full snapshot.
                }
              }
              cut = buffer.indexOf('\n\n');
            }
          }
        } catch (e) {
          if (controller.signal.aborted) return;
          void e;
        }
        if (controller.signal.aborted) return;
        setState('reconnecting');
        await new Promise((r) => setTimeout(r, delay));
        delay = Math.min(delay * 2, 30000);
      }
    };
    void run();
    return () => {
      controller.abort();
      setState('off');
    };
  }, [path]);
  return state;
}
